import React, { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Input } from "../shadcn/ui/input";
import { Label } from "../shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../shadcn/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/ui/popover";
import { Calendar } from "../shadcn/ui/calendar";
import { cn } from "../lib/utils";
import billingService from "../services/billingService";
import * as localService from "../services/localService";
import { showApiMessage } from "../utils/messageHandler";
import logger from "../utils/logger";

const StatementRequestDialog = ({ open, onOpenChange, onSuccess }) => {
  const [formData, setFormData] = useState({
    recipientEmail: "",
    startDate: "",
    endDate: "",
    transactionType: "ALL",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.recipientEmail || formData.recipientEmail.trim() === "") {
      newErrors.recipientEmail = "Recipient email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.recipientEmail)) {
      newErrors.recipientEmail = "Invalid email format";
    }

    if (!formData.startDate || formData.startDate.trim() === "") {
      newErrors.startDate = "Start date is required";
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.startDate)) {
      newErrors.startDate = "Start date must be in YYYY-MM-DD format";
    }

    if (!formData.endDate || formData.endDate.trim() === "") {
      newErrors.endDate = "End date is required";
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.endDate)) {
      newErrors.endDate = "End date must be in YYYY-MM-DD format";
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const currentUser = localService.getUser();
      const payload = {
        organizationId: currentUser.organizationId,
        recipientEmail: formData.recipientEmail,
        startDate: formData.startDate,
        endDate: formData.endDate,
        transactionType: formData.transactionType,
      };

      await billingService.createStatementRequest(payload);

      showApiMessage({ status: 200, message: "Check your email for the statement" }, "request statement");

      setFormData({
        recipientEmail: "",
        startDate: "",
        endDate: "",
        transactionType: "ALL",
      });
      setErrors({});
      onOpenChange(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      logger.error("Error creating statement request:", error);
      showApiMessage(error, "create statement request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Request Statement</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="recipientEmail">
                Recipient Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="recipientEmail"
                type="email"
                placeholder="email@spacia.com"
                value={formData.recipientEmail}
                onChange={e => handleChange("recipientEmail", e.target.value)}
                className={errors.recipientEmail ? "border-red-500" : ""}
              />
              {errors.recipientEmail && <p className="text-sm text-red-500">{errors.recipientEmail}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="startDate">
                  Start Date <span className="text-red-500">*</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.startDate && "text-muted-foreground",
                        errors.startDate && "border-red-500"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.startDate ? format(new Date(formData.startDate), "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.startDate ? new Date(formData.startDate) : undefined}
                      onSelect={date => handleChange("startDate", date ? format(date, "yyyy-MM-dd") : "")}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {errors.startDate && <p className="text-sm text-red-500">{errors.startDate}</p>}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="endDate">
                  End Date <span className="text-red-500">*</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.endDate && "text-muted-foreground",
                        errors.endDate && "border-red-500"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.endDate ? format(new Date(formData.endDate), "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.endDate ? new Date(formData.endDate) : undefined}
                      onSelect={date => handleChange("endDate", date ? format(date, "yyyy-MM-dd") : "")}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {errors.endDate && <p className="text-sm text-red-500">{errors.endDate}</p>}
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="transactionType">
                Transaction Type <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.transactionType} onValueChange={value => handleChange("transactionType", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select transaction type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Transactions</SelectItem>
                  <SelectItem value="CREDIT">Credit</SelectItem>
                  <SelectItem value="DEBIT">Debit</SelectItem>
                  <SelectItem value="REVERSAL">Reversal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StatementRequestDialog;
